import type { ProductCard } from "@ai-guidance/shared";
import { commerceText, type CommerceCopy } from "./text";

/**
 * Cardurile de produs și cele două locuri în care apar:
 *  - chatul scris: în firul conversației, sub replica agentului;
 *  - modul de voce: un panou discret peste pagină (dreapta-jos), care
 *    nu fură focusul și se stinge singur.
 *
 * Tot conținutul intră prin `textContent` - niciun `innerHTML` cu date de
 * catalog. E aceeași regulă ca în `widget.ts`, și aici contează la fel de mult:
 * titlurile și notele trec prin model și prin catalogul clientului.
 */

/** Cât stă panoul de voce pe ecran fără nicio interacțiune. */
export const VOICE_PANEL_TTL_MS = 30_000;

/** Cât stă un toast (adăugat în coș / eroare). */
export const TOAST_TTL_MS = 4_000;

export const COMMERCE_CSS = `
/* Everything here lives in the widget's shadow root, next to WIDGET_CSS: the host
   page cannot restyle it, and the cards inherit the same reset from :host.
   The floating panel is position:fixed, which inside a shadow root still resolves
   against the viewport - the same trick the presence layer uses. */
.aig-cards { display: flex; flex-direction: column; gap: 8px; }
/* Explicit ink, never inherited: the card sits on its own white background on ANY
   skin, and the HUD skin paints the host ink near-white - inherited, that meant
   white titles on a white card (seen live, 11 Aug 2026). */
.aig-card { position: relative; display: flex; gap: 10px; padding: 8px; border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; color: #0f172a; }
.aig-card-img { width: 56px; height: 56px; flex-shrink: 0; object-fit: cover; border-radius: 8px; background: #f1f5f9; }
.aig-card-body { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.aig-card-title { font-size: 12.5px; font-weight: 600; line-height: 1.3; }
.aig-card-variant { font-size: 11px; color: #64748b; }
.aig-card-prices { display: flex; align-items: baseline; gap: 6px; }
.aig-card-price { font-size: 12.5px; font-weight: 600; }
/* The struck-through price is the shop's own compare-at value, so it is shown only
   when the enriched payload carries one - never computed here. */
.aig-card-was { font-size: 11px; color: #94a3b8; text-decoration: line-through; }
.aig-card-note { font-size: 11px; color: #475569; line-height: 1.35; }
.aig-card-actions { display: flex; align-items: center; gap: 8px; margin-top: 4px; flex-wrap: wrap; }
.aig-card-add { appearance: none; -webkit-appearance: none; font-family: inherit; border: none; cursor: pointer; padding: 5px 10px; border-radius: 8px; font-size: 11.5px; font-weight: 600; background: var(--aig-primary, #8b5cf6); color: var(--aig-on-primary, #fff); }
.aig-card-add:hover { background: var(--aig-primary-hover, #7a51d8); }
.aig-card-add[disabled] { background: #e2e8f0; color: #94a3b8; cursor: default; }
.aig-card-link { font-size: 11.5px; color: var(--aig-primary-ink, #7a51d8); text-decoration: none; }
.aig-card-link:hover { text-decoration: underline; }
.aig-card-add:focus-visible, .aig-card-link:focus-visible { outline: 2px solid var(--aig-primary-ink, #7a51d8); outline-offset: 2px; border-radius: 6px; }

/* Voice mode: the cards float over the shop, clear of the launcher (46px + 20px).
   It never takes focus - the visitor is talking, not tabbing - and it fades out
   on its own. */
.aig-float { position: fixed; right: 20px; bottom: 84px; width: 300px; max-width: calc(100vw - 40px); max-height: 60vh; overflow-y: auto; z-index: 2147483030; background: #fff; border-radius: 12px; box-shadow: 0 8px 30px rgba(0,0,0,.25); padding: 10px; }
.aig-float[hidden] { display: none; }
.aig-float-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 6px; }
.aig-float-title { font-size: 11px; font-weight: 600; color: #64748b; }
.aig-float-close { appearance: none; -webkit-appearance: none; font-family: inherit; border: none; background: transparent; color: #94a3b8; font-size: 13px; line-height: 1; cursor: pointer; padding: 2px 4px; }

/* One toast at a time, bottom-right above the launcher: a cart action is
   sub-second, so this is a confirmation, not a progress indicator. */
.aig-toast { position: fixed; right: 20px; bottom: 84px; max-width: min(300px, calc(100vw - 40px)); z-index: 2147483040; background: #0f172a; color: #e2e8f0; font-size: 12px; line-height: 1.4; padding: 8px 12px; border-radius: 10px; box-shadow: 0 6px 20px rgba(0,0,0,.28); }
.aig-toast[hidden] { display: none; }
.aig-toast--error { background: #7f1d1d; color: #fee2e2; }
`;

/** Doar http(s): o imagine `javascript:`/`data:` n-are ce căuta în `<img src>`. */
export function safeImageUrl(url: string | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url, location.href);
    return /^https?:$/.test(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
}

/**
 * „Vezi produsul" e o navigare NORMALĂ, deci calea trebuie să rămână pe situl
 * clientului: acceptăm doar căi relative same-origin (aceeași regulă ca
 * `navigateActionSchema`). Orice altceva → fără link, cardul rămâne util.
 */
export function safeProductPath(path: string): string | null {
  if (!path.startsWith("/") || path.startsWith("//")) return null;
  return path;
}

export interface CardCallbacks {
  /** Click pe „Adaugă în coș": E confirmarea umană, deci cheamă direct adaptorul. */
  onAdd: (card: ProductCard) => void;
}

/** Un card. Fără `innerHTML` nicăieri - vezi antetul fișierului. */
function renderCard(card: ProductCard, cb: CardCallbacks, copy: CommerceCopy): HTMLElement {
  const root = document.createElement("div");
  root.className = "aig-card";
  root.setAttribute("data-aig-variant", card.variantId);

  const image = safeImageUrl(card.imageUrl);
  if (image) {
    const img = document.createElement("img");
    img.className = "aig-card-img";
    img.src = image;
    img.alt = "";
    img.loading = "lazy";
    root.appendChild(img);
  }

  const body = document.createElement("div");
  body.className = "aig-card-body";

  const title = document.createElement("div");
  title.className = "aig-card-title";
  title.textContent = card.title;
  body.appendChild(title);

  if (card.variantTitle) {
    const variant = document.createElement("div");
    variant.className = "aig-card-variant";
    variant.textContent = card.variantTitle;
    body.appendChild(variant);
  }

  const prices = document.createElement("div");
  prices.className = "aig-card-prices";
  const price = document.createElement("span");
  price.className = "aig-card-price";
  price.textContent = card.price;
  prices.appendChild(price);
  if (card.compareAtPrice) {
    const was = document.createElement("span");
    was.className = "aig-card-was";
    was.textContent = card.compareAtPrice;
    prices.appendChild(was);
  }
  body.appendChild(prices);

  if (card.note) {
    const note = document.createElement("div");
    note.className = "aig-card-note";
    note.textContent = card.note;
    body.appendChild(note);
  }

  const actions = document.createElement("div");
  actions.className = "aig-card-actions";
  const add = document.createElement("button");
  add.className = "aig-card-add";
  add.type = "button";
  add.textContent = card.available ? copy.addToCart : copy.soldOut;
  add.disabled = !card.available;
  add.addEventListener("click", () => cb.onAdd(card));
  actions.appendChild(add);

  const href = safeProductPath(card.productPath);
  if (href) {
    const link = document.createElement("a");
    link.className = "aig-card-link";
    link.href = href;
    link.textContent = copy.viewProduct;
    actions.appendChild(link);
  }
  body.appendChild(actions);
  root.appendChild(body);

  // Fără badge de „recomandat": cardul apare fiindcă agentul l-a recomandat -
  // eticheta repeta contextul și se suprapunea peste titlu (scos la cererea
  // lui Radu, 11 aug 2026). `card.recommended` rămâne în wire - marchează
  // alegerea agentului pentru alte suprafețe, nu pentru un badge aici.
  return root;
}

export function renderProductCards(
  cards: ProductCard[],
  cb: CardCallbacks,
  copy: CommerceCopy = commerceText(),
): HTMLElement {
  const list = document.createElement("div");
  list.className = "aig-cards";
  list.setAttribute("role", "group");
  list.setAttribute("aria-label", copy.suggestionsLabel);
  for (const card of cards) list.appendChild(renderCard(card, cb, copy));
  return list;
}

/**
 * Stratul peste pagină al modului de voce: panoul de carduri + toast-urile.
 * Trăiește în shadow root-ul widgetului (deci CSS-ul magazinului nu-l atinge)
 * și nu cere niciodată focusul - vizitatorul vorbește, nu navighează cu Tab.
 */
export class CommerceOverlay {
  private panel: HTMLDivElement;
  private panelBody: HTMLDivElement;
  private toast: HTMLDivElement;
  private panelTimer?: ReturnType<typeof setTimeout>;
  private toastTimer?: ReturnType<typeof setTimeout>;

  constructor(host: { mountOverlay: (node: HTMLElement) => void }, copy: CommerceCopy = commerceText()) {
    this.panel = document.createElement("div");
    this.panel.className = "aig-float";
    this.panel.setAttribute("data-ai-guidance", "commerce-panel");
    this.panel.hidden = true;

    const head = document.createElement("div");
    head.className = "aig-float-head";
    const title = document.createElement("span");
    title.className = "aig-float-title";
    title.textContent = copy.suggestionsLabel;
    const close = document.createElement("button");
    close.className = "aig-float-close";
    close.type = "button";
    close.setAttribute("aria-label", copy.closePanel);
    close.textContent = "✕";
    close.addEventListener("click", () => this.hideCards());
    head.append(title, close);

    this.panelBody = document.createElement("div");
    this.panel.append(head, this.panelBody);

    this.toast = document.createElement("div");
    this.toast.className = "aig-toast";
    this.toast.setAttribute("data-ai-guidance", "commerce-toast");
    // `status`, nu `alert`: e o confirmare, deci se anunță politicos, fără să
    // întrerupă cititorul de ecran în mijlocul unei propoziții.
    this.toast.setAttribute("role", "status");
    this.toast.hidden = true;

    host.mountOverlay(this.panel);
    host.mountOverlay(this.toast);
  }

  showCards(node: HTMLElement): void {
    this.panelBody.replaceChildren(node);
    this.panel.hidden = false;
    clearTimeout(this.panelTimer);
    this.panelTimer = setTimeout(() => this.hideCards(), VOICE_PANEL_TTL_MS);
  }

  /** Se închide singur la următoarea acțiune (o adăugare face panoul irelevant). */
  hideCards(): void {
    clearTimeout(this.panelTimer);
    this.panelTimer = undefined;
    this.panel.hidden = true;
    this.panelBody.replaceChildren();
  }

  showToast(text: string, kind: "ok" | "error" = "ok"): void {
    this.toast.textContent = text;
    this.toast.classList.toggle("aig-toast--error", kind === "error");
    this.toast.hidden = false;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      this.toast.hidden = true;
    }, TOAST_TTL_MS);
  }
}
